import type { FlowLinkContent, FlowRadioGroupContent, FlowRadioOption } from '../types/content';

/**
 * One option of a choice step stored as a child item of the step (ADR-0083), as Sanity does when the
 * options were Sitecore items under the step. `itemName` is that item's name, which is the answer the
 * flow records.
 */
export interface ChoiceItemContent {
  itemName?: string;
  label?: string;
  illustration?: string;
  /** Where choosing the option leads, for an option that ends the flow, as the adapter hands the link over. */
  redirectLink?: FlowLinkContent;
}

/** The part of a step's content this reads: see `readChoiceOptions`. */
export interface ChoiceOptionFields {
  /** The options as a radio group, the way Sitecore sends every choice step. */
  genericContentRadio?: FlowRadioGroupContent;
  /** The options as the step's child items, on Sanity, for a step whose options were Sitecore items. */
  items?: ChoiceItemContent[];
  /** The required message that sits beside those child items. */
  requiredMessageText?: string;
}

/**
 * A choice step's options and required message, from whichever shape the step arrived in.
 *
 * Sitecore sends every choice step's options as a radio group, `genericContentRadio`, with the required
 * message inside it. Sanity stores them one of two ways, depending on where they lived in Sitecore:
 * - fixed options (true or false, yes or no) come as that same radio group;
 * - options that were Sitecore items under the step, such as the legislation compass's employees,
 *   revenue and energy labels questions, come as the step's child items, `items`, each with its answer
 *   in `itemName`, and the required message beside them as `requiredMessageText`.
 *
 * The radio group is read as it comes, so a step that has one renders as it always did. Child items
 * are read only when there is no radio group, and one without an `itemName` is left out, since it
 * could not record an answer. An option keeps the link it leads to in either shape.
 */
export const readChoiceOptions = (
  fields: ChoiceOptionFields | undefined,
): { options: FlowRadioOption[]; requiredMessage?: string } => {
  const group = fields?.genericContentRadio;
  const fromItems = (fields?.items ?? [])
    .filter(item => !!item.itemName)
    .map(({ itemName, label, illustration, redirectLink }) => ({ name: itemName, label, illustration, redirectLink }));

  return {
    options: group?.options?.length ? group.options : fromItems,
    requiredMessage: group?.requiredMessage ?? fields?.requiredMessageText,
  };
};
